import React, { ChangeEvent, useEffect, useState } from "react";

import {
    LOGIN_METHODS,
    storePasswordlessAsLoginMethod,
    useAuth,
    useCurrentUserQuery,
} from "@accurx/auth";
import { Ds, Feedback, FormFieldWrapper, Input } from "@accurx/design";
import { EmailAddressHelper } from "@accurx/shared";
import { cleanUpSession } from "appLifecycle";
import { Redirect, useHistory } from "react-router-dom";
import { useAnalytics } from "reduxQuarantine/useAnalytics";

import { postMagicResendEmail, postPasswordlessLogin } from "api/AccountApi";
import { PUBLIC_ROUTES, ROUTES } from "shared/Routes";

import { InnerStackPanel, OuterStackPanel } from "../LogIn/LoginStackPanel";
import { StyledFullWidthButton } from "../SharedStyledComponents.styles";

enum PAGE_STATES {
    INITIAL,
    VALID,
    INVALID,
    CONFLICT, // Trying to log in while already logged in to a different account
}

export const MagicLink = (): JSX.Element => {
    const history = useHistory();
    const params = new URLSearchParams(history.location.search);
    const email = params.get("email") || "";
    const token = params.get("token") || "";

    const [currentState, setCurrentState] = useState(PAGE_STATES.INITIAL);
    const [emailInput, setEmailInput] = useState(""); // Fallback if no email params detected
    const [emailError, setEmailError] = useState("");
    const [feedbackMsg, setFeedbackMsg] = useState("");
    const [previousAccountEmail, setPreviousAccountEmail] = useState("");

    const { logOut, user } = useAuth();

    const { refetch: refetchUser } = useCurrentUserQuery({
        enabled: false,
    });

    const track = useAnalytics();

    useEffect(() => {
        // Redirect users that are missing query params
        if (!email || !token) {
            setCurrentState(PAGE_STATES.INVALID);
            return;
        }

        const validateMagicLinkTokens = async () => {
            const isUserLoggedIn = "accuRxUserId" in user;

            if (isUserLoggedIn && user.email) {
                // Scenario 1: The user is trying to magic link with an account they're already logged in with. Send them straight to Desktop Launch.
                if (user.email === email) {
                    setCurrentState(PAGE_STATES.VALID);
                    return;
                }

                // Scenario 2: The user is trying to magic link to a different email they're already logged in with. Tell them what their options are.
                setPreviousAccountEmail(user.email);
                setCurrentState(PAGE_STATES.CONFLICT);
                return;
            }

            // Scenario 3: User isn't logged in, carry on with passwordless login:
            const loginRequest = await postPasswordlessLogin({
                email: decodeURIComponent(email),
                passwordlessToken: decodeURIComponent(token),
            });

            if (
                !loginRequest.success ||
                !loginRequest.result ||
                !loginRequest.result.email
            ) {
                setCurrentState(PAGE_STATES.INVALID);
                return;
            }

            if (loginRequest.success) {
                // Update the user in the session
                await refetchUser();

                storePasswordlessAsLoginMethod();
                setCurrentState(PAGE_STATES.VALID);
                return;
            }

            setCurrentState(PAGE_STATES.INVALID);
        };

        validateMagicLinkTokens();
    }, [email, token, refetchUser, user]);

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEmailInput(e.target.value);
    };

    const handleSendMagicLink = async () => {
        setEmailError("");
        const emailParams = emailInput || decodeURIComponent(email);
        const emailErr = EmailAddressHelper.isValidEmailAddress(emailParams);
        if (!emailErr) {
            setEmailError("Please enter a valid email address and try again.");
            return;
        }

        const res = await postMagicResendEmail(emailParams);
        if (!res.success) {
            setFeedbackMsg(
                res.error || "Something went wrong. Please try again.",
            );
            return;
        }

        history.push(`${PUBLIC_ROUTES.checkYourInbox}`, {
            email: emailParams,
        });
    };

    const handleUseOldAccount = () => {
        track("AccountUserExistingLoginRemain Button Click", {
            loginMethod: LOGIN_METHODS.Passwordless,
        });

        setCurrentState(PAGE_STATES.VALID);
    };

    const handleSwitchAccounts = () => {
        track("AccountUserExistingLoginSwitch Button Click", {
            loginMethod: LOGIN_METHODS.Passwordless,
        });

        // log out and return to /magic-link?email&token so we can log them in
        logOut({
            cleanUpFn: cleanUpSession,
            redirectTo: `${PUBLIC_ROUTES.magicLink}?email=${encodeURIComponent(
                email,
            )}&token=${encodeURIComponent(token)}&viaLogOut=true`,
        });
    };

    const getContentToRender = () => {
        switch (currentState) {
            case PAGE_STATES.INITIAL:
                return (
                    <Ds.Flex justifyContent="center">
                        <Ds.Spinner />
                    </Ds.Flex>
                );
            case PAGE_STATES.INVALID:
                return (
                    <>
                        <Ds.Text as="h1" size="xxlarge">
                            Invalid magic link
                        </Ds.Text>
                        <InnerStackPanel horizontalContentAlignment="stretch">
                            <Ds.Text>
                                Magic links expire after 24 hours and can only
                                be used once.
                            </Ds.Text>
                            {(!email || emailError) && (
                                <FormFieldWrapper
                                    label="Email"
                                    labelProps={{ htmlFor: "user-email" }}
                                    errors={[emailError]}
                                >
                                    <Input
                                        id="user-email"
                                        value={emailInput}
                                        type="email"
                                        placeholder={"Email address"}
                                        onChange={handleEmailChange}
                                        hasErrors={!!emailError}
                                        required
                                        autoFocus
                                    />
                                </FormFieldWrapper>
                            )}
                            <StyledFullWidthButton
                                type="button"
                                onClick={handleSendMagicLink}
                                text="Send a new magic link"
                            />
                            {feedbackMsg && (
                                <Feedback title={feedbackMsg} colour="error" />
                            )}
                        </InnerStackPanel>
                    </>
                );
            case PAGE_STATES.CONFLICT:
                track("AccountUserExistingLogin Page View", {
                    loginMethod: LOGIN_METHODS.Passwordless,
                });

                return (
                    <>
                        <Ds.Text as="h1" size="xxlarge">
                            You're already logged in
                        </Ds.Text>
                        <InnerStackPanel horizontalContentAlignment="stretch">
                            <Ds.Text>
                                You're already logged in{" "}
                                {previousAccountEmail
                                    ? `as ${previousAccountEmail}`
                                    : "with a different account"}
                                .
                            </Ds.Text>
                            <StyledFullWidthButton
                                type="button"
                                onClick={handleSwitchAccounts}
                                text={`Switch to ${email}`}
                            />
                            <StyledFullWidthButton
                                type="button"
                                theme="secondary"
                                onClick={handleUseOldAccount}
                                text={`Keep using ${
                                    previousAccountEmail
                                        ? previousAccountEmail
                                        : "your current account"
                                }`}
                            />
                        </InnerStackPanel>
                    </>
                );
            default:
                // Send the user to Desktop Launch, which will trigger 2FA on the way through.
                return <Redirect to={ROUTES.desktopLaunch} />;
        }
    };

    return <OuterStackPanel>{getContentToRender()}</OuterStackPanel>;
};
